import { useCallback, useMemo } from 'react';
/* eslint-disable react-hooks/exhaustive-deps */
export default function useEventDetailsTaskModel(context) {
  const {
    EVENT_TASK_FOCUS_LIMIT,
    TASK_PRIORITY_ORDER,
    TASK_STATUS_LABELS,
    calcDueDays,
    eventData,
    eventTaskAllTasks,
    eventTaskAssignableAdmins,
    eventTaskDependencies,
    eventTaskDependencyLinkType,
    eventTaskDependencyQuery,
    eventTaskDetailModal,
    eventTaskNewSubtaskAssigneeId,
    eventTaskProject,
    eventTaskRecentTasks,
    eventTaskSections,
    formatDate,
    formatDueHint,
    getDueTone,
    getEventTaskAssigneeAvatarUrl,
    getEventTaskAssigneeLabel,
    getEventTaskPrimaryAssignee,
    isTaskDoneStatus,
    normalizeEventTaskStatus,
    normalizeTaskPriority,
    setEventTaskAllTasks,
    setEventTaskRecentTasks,
    toTimestamp,
  } = context;
    const normalizeEventTaskFromApi = useCallback((task) => {
      if (!task || typeof task !== 'object') return null;
      return {
        ...task,
        status: String(task?.status || '').trim().toLowerCase() || 'todo',
        priority: normalizeTaskPriority(task?.priority),
        assignees: Array.isArray(task?.assignees) ? task.assignees : [],
      };
    }, []);

    const applyEventTaskToState = useCallback(
      (nextTaskInput) => {
        const normalized = normalizeEventTaskFromApi(nextTaskInput);
        if (!normalized?.id) return;
        const targetId = String(normalized.id);
        setEventTaskAllTasks((prev) => {
          const found = prev.some((task) => String(task.id) === targetId);
          const merged = found
            ? prev.map((task) => (String(task.id) === targetId ? { ...task, ...normalized } : task))
            : [normalized, ...prev];
          return merged;
        });
        setEventTaskRecentTasks((prev) =>
          prev.map((task) => (String(task.id) === targetId ? { ...task, ...normalized } : task))
        );
      },
      [normalizeEventTaskFromApi]
    );

    const removeEventTaskFromState = useCallback((taskId) => {
      const targetId = String(taskId || '').trim();
      if (!targetId) return;
      setEventTaskAllTasks((prev) => prev.filter((task) => String(task.id) !== targetId));
      setEventTaskRecentTasks((prev) => prev.filter((task) => String(task.id) !== targetId));
    }, []);

    const formatTaskDateInput = useCallback((value) => {
      if (!value) return '';
      const raw = String(value).trim();
      if (!raw) return '';
      if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
      return raw.slice(0, 10);
    }, []);

    // JSTの「今日」のYYYY-MM-DDを算出
    const jstTodayYmd = useMemo(() => {
      const f = new Intl.DateTimeFormat('ja-JP', {
        timeZone: 'Asia/Tokyo',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
      const parts = f.formatToParts(new Date());
      const y = parts.find(p => p.type === 'year')?.value;
      const m = parts.find(p => p.type === 'month')?.value;
      const d = parts.find(p => p.type === 'day')?.value;
      return `${y}-${m}-${d}`.replace(/\//g, '-');
    }, []);

    const isToday = useMemo(() => {
      const ymd = String(eventData?.event_date || '').slice(0, 10);
      return ymd && ymd === jstTodayYmd;
    }, [eventData, jstTodayYmd]);
    const isTodayDate = useCallback(
      (dateValue) => {
        const ymd = String(dateValue || '').slice(0, 10);
        return Boolean(ymd) && ymd === jstTodayYmd;
      },
      [jstTodayYmd]
    );

    const eventTaskInsights = useMemo(() => {
      const sourceTasks = eventTaskAllTasks.length ? eventTaskAllTasks : eventTaskRecentTasks;
      const totalFromProject = Number(eventTaskProject?.totalTaskCount || 0);
      const doneFromProject = Number(eventTaskProject?.doneTaskCount || 0);
      const openFromProject = Number(eventTaskProject?.openTaskCount || 0);

      let totalCount = totalFromProject;
      let doneCount = doneFromProject;
      let openCount = openFromProject;

      if (totalCount <= 0) {
        totalCount = sourceTasks.length;
      }
      if (doneCount <= 0 && totalCount > 0 && openCount > 0) {
        doneCount = Math.max(0, totalCount - openCount);
      }
      if (openCount <= 0 && totalCount > 0) {
        openCount = Math.max(0, totalCount - doneCount);
      }

      const priorityCounts = { urgent: 0, high: 0, normal: 0, low: 0 };
      let overdueCount = 0;
      let dueTodayCount = 0;
      let dueWeekCount = 0;

      const focusCandidates = [];

      sourceTasks.forEach((task) => {
        const statusKey = String(task?.status || '')
          .trim()
          .toLowerCase();
        if (isTaskDoneStatus(statusKey)) return;

        const priorityKey = normalizeTaskPriority(task?.priority);
        priorityCounts[priorityKey] = Number(priorityCounts[priorityKey] || 0) + 1;

        const dueDays = calcDueDays(task?.dueOn, jstTodayYmd);
        if (dueDays !== null) {
          if (dueDays < 0) overdueCount += 1;
          if (dueDays === 0) dueTodayCount += 1;
          if (dueDays >= 0 && dueDays <= 7) dueWeekCount += 1;
        }

        const isHighPriority = priorityKey === 'urgent' || priorityKey === 'high';
        const isNearDue = dueDays !== null && dueDays <= 14;
        if (!isHighPriority && !isNearDue) return;

        const dueScore =
          dueDays === null
            ? 0
            : dueDays < 0
            ? 600 + Math.min(240, Math.abs(dueDays) * 24)
            : dueDays === 0
            ? 520
            : dueDays <= 3
            ? 500 - dueDays * 22
            : dueDays <= 7
            ? 430 - dueDays * 10
            : 320 - dueDays * 6;
        const priorityScore = (TASK_PRIORITY_ORDER[priorityKey] || 0) * 180;

        focusCandidates.push({
          ...task,
          priorityKey,
          dueDays,
          dueTone: getDueTone(dueDays),
          dueLabel: task?.dueOn ? formatDate(task.dueOn) : '期限未設定',
          dueHint: formatDueHint(dueDays),
          assignee: getEventTaskPrimaryAssignee(task),
          assigneeLabel: getEventTaskAssigneeLabel(task),
          assigneeAvatarUrl: getEventTaskAssigneeAvatarUrl(task),
          focusScore: dueScore + priorityScore,
        });
      });

      focusCandidates.sort((a, b) => {
        if (b.focusScore !== a.focusScore) return b.focusScore - a.focusScore;
        if (a.dueDays === null && b.dueDays !== null) return 1;
        if (a.dueDays !== null && b.dueDays === null) return -1;
        if (a.dueDays !== null && b.dueDays !== null && a.dueDays !== b.dueDays) {
          return a.dueDays - b.dueDays;
        }
        const aUpdated = toTimestamp(a.updatedAt) || 0;
        const bUpdated = toTimestamp(b.updatedAt) || 0;
        return bUpdated - aUpdated;
      });

      const focusTasks = focusCandidates.slice(0, EVENT_TASK_FOCUS_LIMIT);
      const completionRate = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;
      const openTaskCount = Number(openCount || Math.max(0, totalCount - doneCount));
      const doneTaskCount = Number(doneCount || Math.max(0, totalCount - openTaskCount));
      const totalTaskCount = Number(totalCount || doneTaskCount + openTaskCount);

      return {
        totalTaskCount,
        openTaskCount,
        doneTaskCount,
        completionRate,
        priorityCounts,
        overdueCount,
        dueTodayCount,
        dueWeekCount,
        focusTasks,
      };
    }, [eventTaskAllTasks, eventTaskRecentTasks, eventTaskProject, formatDate, jstTodayYmd]);

    const eventTaskMergedList = useMemo(() => {
      const map = new Map();
      eventTaskRecentTasks.forEach((task) => {
        if (!task?.id) return;
        map.set(String(task.id), task);
      });
      eventTaskAllTasks.forEach((task) => {
        if (!task?.id) return;
        map.set(String(task.id), task);
      });
      return Array.from(map.values());
    }, [eventTaskAllTasks, eventTaskRecentTasks]);

    const eventTaskDetailTask = useMemo(() => {
      const targetId = String(eventTaskDetailModal?.taskId || '').trim();
      if (!targetId) return null;
      return eventTaskMergedList.find((task) => String(task.id) === targetId) || null;
    }, [eventTaskDetailModal, eventTaskMergedList]);

    const eventTaskDetailSubtasks = useMemo(() => {
      if (!eventTaskDetailTask?.id) return [];
      const parentId = String(eventTaskDetailTask.id);
      return eventTaskAllTasks
        .filter((task) => String(task?.parentTaskId || '') === parentId)
        .sort((a, b) => {
          const ao = Number(a?.sortOrder || 0);
          const bo = Number(b?.sortOrder || 0);
          if (ao !== bo) return ao - bo;
          const au = toTimestamp(a?.updatedAt) || 0;
          const bu = toTimestamp(b?.updatedAt) || 0;
          return bu - au;
        });
    }, [eventTaskAllTasks, eventTaskDetailTask]);

    const eventTaskSectionNameMap = useMemo(() => {
      const map = new Map();
      eventTaskSections.forEach((section) => {
        if (!section?.id) return;
        map.set(String(section.id), String(section.name || '未分類'));
      });
      return map;
    }, [eventTaskSections]);

    const eventTaskDetailSectionName = useMemo(() => {
      const sectionId = String(eventTaskDetailTask?.sectionId || '').trim();
      if (!sectionId) return '未分類';
      return eventTaskSectionNameMap.get(sectionId) || '未分類';
    }, [eventTaskDetailTask?.sectionId, eventTaskSectionNameMap]);

    const eventTaskDetailDueMeta = useMemo(() => {
      const dueDays = calcDueDays(eventTaskDetailTask?.dueOn, jstTodayYmd);
      return {
        tone: getDueTone(dueDays),
        label: eventTaskDetailTask?.dueOn ? formatDate(eventTaskDetailTask.dueOn) : '期限未設定',
        hint: formatDueHint(dueDays),
      };
    }, [eventTaskDetailTask?.dueOn, formatDate, jstTodayYmd]);

    const eventTaskDetailParentTask = useMemo(() => {
      const parentId = String(eventTaskDetailTask?.parentTaskId || '').trim();
      if (!parentId) return null;
      return eventTaskMergedList.find((task) => String(task.id) === parentId) || null;
    }, [eventTaskDetailTask?.parentTaskId, eventTaskMergedList]);

    const eventTaskDetailAssigneeLabel = useMemo(
      () => getEventTaskAssigneeLabel(eventTaskDetailTask),
      [eventTaskDetailTask]
    );
    const eventTaskDetailAssigneeAvatarUrl = useMemo(
      () => getEventTaskAssigneeAvatarUrl(eventTaskDetailTask),
      [eventTaskDetailTask]
    );
    const eventTaskDetailHasAssignee = useMemo(
      () => Boolean(getEventTaskPrimaryAssignee(eventTaskDetailTask)),
      [eventTaskDetailTask]
    );
    const eventTaskNewSubtaskAssigneeLabel = useMemo(() => {
      const targetId = String(eventTaskNewSubtaskAssigneeId || '').trim();
      if (!targetId) return '未割当';
      const matched = eventTaskAssignableAdmins.find(
        (admin) => String(admin?.adminUserId || '') === targetId
      );
      const label =
        matched?.displayName ||
        matched?.display_name ||
        matched?.fullName ||
        matched?.full_name ||
        matched?.name ||
        matched?.email ||
        '';
      return String(label || '').trim() || '未割当';
    }, [eventTaskAssignableAdmins, eventTaskNewSubtaskAssigneeId]);
    const eventTaskDependencyCandidateTasks = useMemo(() => {
      const selectedTaskId = String(eventTaskDetailTask?.id || '').trim();
      if (!selectedTaskId) return [];

      const existingIds = new Set(
        (
          eventTaskDependencyLinkType === 'predecessor'
            ? eventTaskDependencies.predecessors
            : eventTaskDependencies.successors
        ).map((task) => String(task?.id || ''))
      );
      const query = String(eventTaskDependencyQuery || '').trim().toLowerCase();

      return eventTaskAllTasks
        .filter((task) => {
          const taskId = String(task?.id || '').trim();
          if (!taskId || taskId === selectedTaskId) return false;
          return !existingIds.has(taskId);
        })
        .filter((task) => {
          if (!query) return true;
          const text = [
            task?.title || '',
            eventTaskSectionNameMap.get(String(task?.sectionId || '')) || '未分類',
            getEventTaskAssigneeLabel(task),
            TASK_STATUS_LABELS[normalizeEventTaskStatus(task?.status)] || '未着手',
          ]
            .join(' ')
            .toLowerCase();
          return text.includes(query);
        })
        .sort((a, b) => {
          const sortA = Number(a?.sortOrder || 0);
          const sortB = Number(b?.sortOrder || 0);
          if (sortA !== sortB) return sortA - sortB;
          const updatedA = toTimestamp(a?.updatedAt) || 0;
          const updatedB = toTimestamp(b?.updatedAt) || 0;
          return updatedB - updatedA;
        })
        .slice(0, 30);
    }, [
      eventTaskAllTasks,
      eventTaskDependencies.predecessors,
      eventTaskDependencies.successors,
      eventTaskDependencyLinkType,
      eventTaskDependencyQuery,
      eventTaskDetailTask?.id,
      eventTaskSectionNameMap,
    ]);
  return {
    normalizeEventTaskFromApi,
    applyEventTaskToState,
    removeEventTaskFromState,
    formatTaskDateInput,
    jstTodayYmd,
    isToday,
    isTodayDate,
    eventTaskInsights,
    eventTaskMergedList,
    eventTaskDetailTask,
    eventTaskDetailSubtasks,
    eventTaskSectionNameMap,
    eventTaskDetailSectionName,
    eventTaskDetailDueMeta,
    eventTaskDetailParentTask,
    eventTaskDetailAssigneeLabel,
    eventTaskDetailAssigneeAvatarUrl,
    eventTaskDetailHasAssignee,
    eventTaskNewSubtaskAssigneeLabel,
    eventTaskDependencyCandidateTasks,
  };
}
